import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { ArrowDownUp, Briefcase, CalendarClock, Clock, Minus, Plus, Repeat, Route as RouteIcon, Users, Zap } from 'lucide-react';
import type { LatLng, PlaceInput as Place } from '@tcrd/shared';
import { Button, Card, cn, Notice, TextArea } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { PlaceInput } from '../../components/PlaceInput';
import { MapView } from '../../components/map/MapView';
import type { MarkerSpec } from '../../components/map/engine';
import { ServiceIcon } from '../../components/ServiceIcon';
import { DayAvailability } from '../../components/DayAvailability';
import { useToast } from '../../components/toast';
import { api, errorMessage, newIdempotencyKey } from '../../lib/api';
import { currentPosition } from '../../lib/location';
import { fmtDuration, fmtKm, fromLocalInput, toLocalInput } from '../../lib/format';
import { normalizePhone } from '../auth/PhoneGate';
import type { RideRequestDto, ServiceDto } from '../../lib/types';
import { NEEDS } from '../../lib/needs';
import { formatMoney } from '@tcrd/shared';
import { TextField } from '@tcrd/ui';

type Field = 'origin' | 'destination' | number;

function Stepper({ label, icon, value, min, max, onChange }: { label: string; icon: React.ReactNode; value: number; min: number; max: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center justify-between rounded-2xl border border-[var(--border)] bg-[var(--surface)] px-4 h-14">
      <span className="flex items-center gap-2 text-sm font-medium">{icon} {label}</span>
      <div className="flex items-center gap-1">
        <button type="button" className="tap flex items-center justify-center rounded-full disabled:opacity-30" disabled={value <= min} onClick={() => onChange(value - 1)} aria-label={`Menos ${label}`}><Minus className="size-4" /></button>
        <span className="w-6 text-center font-semibold tabular-nums" aria-live="polite">{value}</span>
        <button type="button" className="tap flex items-center justify-center rounded-full disabled:opacity-30" disabled={value >= max} onClick={() => onChange(value + 1)} aria-label={`Más ${label}`}><Plus className="size-4" /></button>
      </div>
    </div>
  );
}

export function RequestRidePage() {
  const history = useHistory();
  const toast = useToast();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const services = useQuery({ queryKey: ['services'], queryFn: () => api<ServiceDto[]>('/services') });
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [mode, setMode] = useState<'NOW' | 'SCHEDULED'>(params.get('mode') === 'SCHEDULED' ? 'SCHEDULED' : 'NOW');
  const fromParam = (k: 'from' | 'to'): Place | null => {
    const lat = Number(params.get(`${k}Lat`));
    const lng = Number(params.get(`${k}Lng`));
    const address = params.get(`${k}Addr`);
    return address && Number.isFinite(lat) && Number.isFinite(lng) && lat && lng ? { address, lat, lng } : null;
  };
  const [origin, setOrigin] = useState<Place | null>(() => fromParam('from'));
  const [destination, setDestination] = useState<Place | null>(() => fromParam('to'));
  const [stops, setStops] = useState<Array<Place | null>>([]);
  const [picking, setPicking] = useState<Field | null>(null);
  const [scheduled, setScheduled] = useState(() => toLocalInput(new Date(Date.now() + 2 * 3600_000)));
  const [roundTrip, setRoundTrip] = useState(false);
  const [returnAt, setReturnAt] = useState(() => toLocalInput(new Date(Date.now() + 6 * 3600_000)));
  const [passengers, setPassengers] = useState(1);
  const [luggage, setLuggage] = useState(0);
  const [notes, setNotes] = useState('');
  // Precio que ofrece el cliente (opcional), vuelo, necesidades y datos de mensajería.
  const [offer, setOffer] = useState('');
  const [flight, setFlight] = useState('');
  const [needs, setNeeds] = useState<string[] | null>(null);
  const [pkg, setPkg] = useState({ description: '', recipientName: '', recipientPhone: '' });
  const safety = useQuery({ queryKey: ['safety-settings'], queryFn: () => api<{ pinEnabled: boolean; needs: string[] }>('/customers/me/safety') });
  const chosenNeeds = needs ?? safety.data?.needs ?? [];
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idemKey = useRef(newIdempotencyKey());

  // Recogida por defecto: la ubicación actual. Si falla (permiso, GPS), se queda vacío sin mostrar error.
  const autoOrigin = useRef(false);
  useEffect(() => {
    if (autoOrigin.current) return;
    autoOrigin.current = true;
    void (async () => {
      try {
        const pos = await currentPosition();
        const d = await api<{ address: string }>('/routes/reverse', { query: { lat: pos.lat, lng: pos.lng } });
        setOrigin((cur) => cur ?? { address: d.address, lat: pos.lat, lng: pos.lng });
      } catch {
        /* el cliente la escribe o la elige en el mapa */
      }
    })();
  }, []);

  useEffect(() => {
    if (!serviceId && services.data?.length) {
      const pre = params.get('service');
      setServiceId(services.data.find((s) => s.code === pre)?.id ?? services.data[0]!.id);
    }
  }, [services.data, serviceId, params]);

  const pt = (p: { lat: number; lng: number }) => ({ lat: p.lat, lng: p.lng });
  const validStops = stops.filter((s): s is Place => !!s);
  const estimate = useQuery({
    queryKey: ['estimate', origin?.lat, origin?.lng, destination?.lat, destination?.lng, validStops.map((s) => `${s.lat},${s.lng}`).join('|')],
    queryFn: () =>
      api<{ provider: string; distanceMeters: number; durationSeconds: number; polyline: string; approximate: boolean }>('/routes/estimate', {
        // Solo coordenadas: el endpoint rechaza campos extra (address).
        body: { origin: pt(origin!), destination: pt(destination!), stops: validStops.map(pt) },
      }),
    enabled: !!origin && !!destination,
    staleTime: 5 * 60_000,
  });

  const service = services.data?.find((s) => s.id === serviceId);
  const isCourier = service?.code === 'COURIER';
  const isAirport = service?.code === 'AIRPORT';
  const suggest = useQuery({
    queryKey: ['suggest-fare', serviceId, origin?.lat, origin?.lng, destination?.lat, destination?.lng, validStops.map((s) => `${s.lat},${s.lng}`).join('|'), roundTrip],
    queryFn: () => api<{ suggested: string }>('/ride-requests/suggest-fare', { body: { serviceTypeId: serviceId, origin: pt(origin!), destination: pt(destination!), stops: validStops.map(pt), roundTrip } }),
    enabled: !!serviceId && !!origin && !!destination,
    staleTime: 5 * 60_000,
  });
  const suggested = suggest.data ? Math.round(Number(suggest.data.suggested)) : null;

  const markers = useMemo<MarkerSpec[]>(
    () => [
      ...(origin ? [{ id: 'o', position: origin, kind: 'origin' as const }] : []),
      ...validStops.map((s, i) => ({ id: `s${i}`, position: s, kind: 'stop' as const, label: String(i + 1) })),
      ...(destination ? [{ id: 'd', position: destination, kind: 'destination' as const }] : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [origin, destination, JSON.stringify(validStops)],
  );

  const onMapClick = async (p: LatLng) => {
    if (picking === null) return;
    const target = picking;
    setPicking(null);
    let address = `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`;
    try {
      address = (await api<{ address: string }>('/routes/reverse', { query: { lat: p.lat, lng: p.lng } })).address;
    } catch {
      /* se usa la coordenada */
    }
    const place = { address, lat: p.lat, lng: p.lng };
    if (target === 'origin') setOrigin(place);
    else if (target === 'destination') setDestination(place);
    else setStops((s) => s.map((x, i) => (i === target ? place : x)));
  };

  const submit = async () => {
    setError(null);
    if (!serviceId || !origin || !destination) return setError('Completa el servicio, el origen y el destino');
    if (isCourier && (!pkg.description.trim() || !pkg.recipientName.trim())) return setError('Indica qué envías y a quién se entrega');
    const recipientPhone = pkg.recipientPhone.trim() ? normalizePhone(pkg.recipientPhone) : null;
    if (isCourier && pkg.recipientPhone.trim() && !recipientPhone) return setError('Revisa el teléfono de quien recibe');
    setSubmitting(true);
    try {
      const body = {
        serviceTypeId: serviceId,
        mode,
        origin,
        destination,
        stops: validStops,
        passengers: isCourier ? 1 : passengers,
        offeredFare: offer && Number(offer) > 0 ? String(Math.round(Number(offer))) : undefined,
        flightNumber: isAirport && flight.trim() ? flight.trim() : undefined,
        needs: chosenNeeds,
        ...(isCourier ? { packageDescription: pkg.description.trim(), recipientName: pkg.recipientName.trim(), recipientPhone: recipientPhone ?? undefined } : {}),
        luggage,
        notes: notes || undefined,
        scheduledAt: mode === 'SCHEDULED' ? fromLocalInput(scheduled) : undefined,
        roundTrip,
        returnAt: roundTrip ? fromLocalInput(returnAt) : undefined,
      };
      const res = await api<RideRequestDto>('/ride-requests', { body, idempotencyKey: idemKey.current });
      toast('Solicitud enviada. Te avisaremos cuando llegue la cotización.', 'success');
      idemKey.current = newIdempotencyKey();
      history.replace(`/app/requests/${res.id}`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page title="Solicitar viaje" back="/app/home">
      <div className="flex flex-col gap-4">
        <MapView className="h-56 sm:h-72 -mx-4 sm:mx-0 sm:rounded-3xl" markers={markers} route={estimate.data?.polyline ?? null} onMapClick={picking !== null ? onMapClick : undefined} />
        {picking !== null && <Notice>Toca el mapa para marcar el punto.</Notice>}

        <div role="radiogroup" aria-label="Cuándo" className="grid grid-cols-2 gap-2">
          {([
            ['NOW', 'Ahora', Zap],
            ['SCHEDULED', 'Programar', CalendarClock],
          ] as const).map(([m, l, Icon]) => (
            <button key={m} role="radio" aria-checked={mode === m} onClick={() => setMode(m)} className={cn('tap h-12 rounded-2xl border flex items-center justify-center gap-2 font-semibold', mode === m ? 'bg-brand-black text-white border-brand-black dark:bg-white dark:text-brand-black' : 'border-[var(--border)] bg-[var(--surface)]')}>
              <Icon className="size-5" aria-hidden /> {l}
            </button>
          ))}
        </div>
        {mode === 'SCHEDULED' && (
          <label className="flex items-center gap-3 rounded-2xl border border-[var(--border)] bg-[var(--surface)] px-4 h-14">
            <Clock className="size-5 text-[var(--text-muted)]" aria-hidden />
            <span className="sr-only">Fecha y hora</span>
            <input type="datetime-local" value={scheduled} min={toLocalInput(new Date(Date.now() + 30 * 60_000))} onChange={(e) => setScheduled(e.target.value)} className="flex-1 bg-transparent outline-none" />
          </label>
        )}
        {mode === 'SCHEDULED' && <DayAvailability localDateTime={scheduled} />}

        <div className="relative flex flex-col gap-2">
          <PlaceInput label="¿Dónde te recogemos?" value={origin} onChange={setOrigin} onPickOnMap={() => setPicking('origin')} dotClass="bg-brand-black dark:bg-white" />
          {stops.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1">
                <PlaceInput label={`Parada ${i + 1}`} value={s} onChange={(p) => setStops((x) => x.map((y, j) => (j === i ? p : y)))} onPickOnMap={() => setPicking(i)} dotClass="bg-gray-400" allowCurrentLocation={false} />
              </div>
              <button className="tap flex items-center justify-center text-[var(--text-muted)]" onClick={() => setStops((x) => x.filter((_, j) => j !== i))} aria-label={`Quitar parada ${i + 1}`}><Minus className="size-5" /></button>
            </div>
          ))}
          <PlaceInput label="¿A dónde vas?" value={destination} onChange={setDestination} onPickOnMap={() => setPicking('destination')} dotClass="bg-brand-red" allowCurrentLocation={false} />
          <div className="flex justify-between">
            <button type="button" className="tap text-sm font-medium text-[var(--text-muted)] flex items-center gap-1" disabled={stops.length >= 3} onClick={() => setStops((s) => [...s, null])}>
              <Plus className="size-4" aria-hidden /> Agregar parada
            </button>
            <button type="button" className="tap text-sm font-medium text-[var(--text-muted)] flex items-center gap-1" onClick={() => (setOrigin(destination), setDestination(origin))} aria-label="Invertir origen y destino">
              <ArrowDownUp className="size-4" aria-hidden /> Invertir
            </button>
          </div>
        </div>

        {estimate.data && (
          <Card className="p-4 flex items-center gap-4">
            <RouteIcon className="size-6 text-brand-red" aria-hidden />
            <div className="flex-1">
              <p className="font-semibold">{fmtKm(estimate.data.distanceMeters)} · {fmtDuration(estimate.data.durationSeconds)}</p>
              <p className="text-xs text-[var(--text-muted)]">{estimate.data.approximate ? 'Estimación aproximada (modo desarrollo sin proveedor de rutas)' : estimate.data.provider === 'TOMTOM' ? 'Distancia por carretera y tiempo con el tráfico actual' : 'Distancia por carretera y tiempo estimado'}</p>
            </div>
          </Card>
        )}
        {estimate.error && <Notice tone="warning">{errorMessage(estimate.error)}</Notice>}

        <section>
          <h2 className="text-sm font-semibold mb-2">Servicio</h2>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 snap-x">
            {services.data?.map((s) => (
              <button key={s.id} onClick={() => setServiceId(s.id)} aria-pressed={serviceId === s.id} className={cn('tap snap-start shrink-0 w-32 rounded-2xl border p-3 text-left', serviceId === s.id ? 'border-brand-red bg-brand-red/5' : 'border-[var(--border)] bg-[var(--surface)]')}>
                <ServiceIcon name={s.icon} className="size-6 mb-2" />
                <span className="block text-sm font-medium leading-tight">{s.name}</span>
              </button>
            ))}
          </div>
        </section>

        {isCourier && (
          <section className="flex flex-col gap-3 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4">
            <h2 className="text-sm font-semibold">Datos del envío</h2>
            <TextField label="¿Qué envías?" placeholder="Ej.: Sobre con documentos, una caja pequeña" value={pkg.description} onChange={(e) => setPkg({ ...pkg, description: e.target.value })} maxLength={300} />
            <div className="grid sm:grid-cols-2 gap-3">
              <TextField label="¿Quién lo recibe?" value={pkg.recipientName} onChange={(e) => setPkg({ ...pkg, recipientName: e.target.value })} maxLength={120} />
              <TextField label="Teléfono de quien recibe" type="tel" inputMode="tel" placeholder="809 555 1234" value={pkg.recipientPhone} onChange={(e) => setPkg({ ...pkg, recipientPhone: e.target.value })} />
            </div>
            <p className="text-xs text-[var(--text-muted)]">Tu conductor tomará una foto al entregarlo; la verás en tu viaje.</p>
          </section>
        )}
        {isAirport && (
          <TextField label="Número de vuelo (si te recogemos en el aeropuerto)" placeholder="Ej.: AA1234" autoCapitalize="characters" value={flight} onChange={(e) => setFlight(e.target.value)} hint="Tu conductor sigue el vuelo y te espera si se atrasa." />
        )}

        <section className="flex flex-col gap-2 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4">
          <h2 className="text-sm font-semibold">Tu precio (opcional)</h2>
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold">RD$</span>
            <input type="number" inputMode="numeric" min={0} placeholder={suggested ? String(suggested) : 'Ofrece tu precio'} value={offer} onChange={(e) => setOffer(e.target.value)} className="flex-1 min-w-0 h-12 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3 text-lg" aria-label="Precio que ofreces" />
          </div>
          {suggested !== null && (
            <div className="flex gap-2 flex-wrap">
              {[Math.round(suggested * 0.9), suggested, Math.round(suggested * 1.1)].map((v, i) => (
                <button key={i} type="button" onClick={() => setOffer(String(v))} className={cn('tap h-9 px-3 rounded-full border text-sm font-medium', offer === String(v) ? 'bg-brand-black text-white border-brand-black dark:bg-white dark:text-brand-black' : 'border-[var(--border)]')}>
                  {formatMoney(String(v))}{i === 1 ? ' · sugerido' : ''}
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-[var(--text-muted)]">Tu conductor puede aceptar tu precio o enviarte el suyo. Nada se cobra hasta que aceptes.</p>
        </section>

        <section>
          <h2 className="text-sm font-semibold mb-2">¿Necesitas algo especial?</h2>
          <div className="flex gap-2 flex-wrap">
            {NEEDS.map((n) => {
              const on = chosenNeeds.includes(n.id);
              return (
                <button key={n.id} type="button" aria-pressed={on} onClick={() => setNeeds(on ? chosenNeeds.filter((x) => x !== n.id) : [...chosenNeeds, n.id])} className={cn('tap h-9 px-3 rounded-full border text-sm', on ? 'border-brand-red bg-brand-red/10 font-semibold' : 'border-[var(--border)] bg-[var(--surface)]')}>
                  {n.emoji} {n.label}
                </button>
              );
            })}
          </div>
        </section>

        <div className={cn('grid sm:grid-cols-2 gap-2', isCourier && 'hidden')}>
          <Stepper label="Pasajeros" icon={<Users className="size-5" aria-hidden />} value={passengers} min={1} max={15} onChange={setPassengers} />
          <Stepper label="Maletas" icon={<Briefcase className="size-5" aria-hidden />} value={luggage} min={0} max={15} onChange={setLuggage} />
        </div>

        <label className="tap flex items-center gap-3 rounded-2xl border border-[var(--border)] bg-[var(--surface)] px-4 h-14">
          <Repeat className="size-5 text-[var(--text-muted)]" aria-hidden />
          <span className="flex-1 text-sm font-medium">Ida y vuelta</span>
          <input type="checkbox" checked={roundTrip} onChange={(e) => setRoundTrip(e.target.checked)} className="size-5 accent-[#E5252A]" />
        </label>
        {roundTrip && (
          <label className="flex items-center gap-3 rounded-2xl border border-[var(--border)] bg-[var(--surface)] px-4 h-14">
            <span className="text-sm text-[var(--text-muted)]">Regreso</span>
            <input type="datetime-local" value={returnAt} onChange={(e) => setReturnAt(e.target.value)} className="flex-1 bg-transparent outline-none" />
          </label>
        )}

        <TextArea label="Observaciones (opcional)" placeholder="Número de vuelo, referencias, necesidades especiales…" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} />

        {error && <Notice tone="danger">{error}</Notice>}
        <Button variant="accent" size="lg" block loading={submitting} disabled={!origin || !destination || !serviceId} onClick={submit}>
          Solicitar cotización
        </Button>
        <p className="text-xs text-center text-[var(--text-muted)]">El conductor te enviará el precio. Nada se cobra hasta que aceptes.</p>
      </div>
    </Page>
  );
}
